import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { writeCart } from "../../modules/checkoutHelpers";
import { sendSlackMessageTracked } from "../../models/notifications";

// ─── Plan data ───────────────────────────────────────────────────────────────

const STARTER = {
    productId: 20,
    name: "Starter",
    monthly: "$49",
    annual: "$39",
    features: [
        "1,000 customer accounts",
        "1 Shopify store",
        "5,000 credit events / mo",
        "Unlimited credit issued",
        "Unlimited team accounts",
        "Unlimited email notifications",
        "Chat support",
    ],
};

const GROWTH_TIERS = {
    S: { productId: 21, monthly: "$149", annual: "$119", accounts: "10,000", stores: "3", events: "16,000", sms: "2,000 SMS / mo" },
    M: { productId: 22, monthly: "$299", annual: "$239", accounts: "25,000", stores: "5", events: "40,000", sms: "8,000 SMS / mo" },
    L: { productId: 23, monthly: "$549", annual: "$439", accounts: "60,000", stores: "10", events: "96,000", sms: "25,000 SMS / mo" },
};

const GROWTH_SHARED_FEATURES = [
    "Unlimited credit issued",
    "Unlimited team accounts",
    "Unlimited email notifications",
    "Full API access",
    "Chat 24/7 + priority support",
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function Check() {
    return (
        <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <polyline points="20 6 9 17 4 12"/>
        </svg>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ModernPricingView() {
    const [billing, setBilling] = useState("annually");
    const [growthTier, setGrowthTier] = useState("M");
    const { isSignedIn } = useAuth();
    const navigate = useNavigate();

    const annual = billing === "annually";
    const tier = GROWTH_TIERS[growthTier];

    const handleSelect = (productId, planLabel) => {
        sendSlackMessageTracked(`Clicked pricing card: ${planLabel}`);
        if (isSignedIn) {
            writeCart({
                productLineItems: [{ productIndex: productId, frequency: annual ? "year" : "month", quantity: 1 }],
                isMonthlyFrequency: !annual,
            });
            navigate("/cart");
        } else {
            window.location.href = "https://app.pabloo.com/sign-up";
        }
    };

    return (
        <div style={{ background: "#0a0a0a", padding: "80px 24px", fontFamily: "'Inter', system-ui, sans-serif" }}>
            <div style={{ maxWidth: "900px", margin: "0 auto" }}>

                {/* Header */}
                <div style={{ textAlign: "center", marginBottom: "48px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        Pricing
                    </p>
                    <h2 style={{ fontSize: "36px", fontWeight: 800, color: "#fff", lineHeight: 1.15, marginBottom: "14px" }}>
                        Flat rate. No surprises.
                    </h2>
                    <p style={{ fontSize: "15px", color: "#6b7280", maxWidth: "420px", margin: "0 auto 28px", lineHeight: 1.6 }}>
                        One fixed price per month. As many orders, as much credit issued, as many team members as you need.
                    </p>

                    {/* Billing toggle */}
                    <div style={{ display: "inline-flex", alignItems: "center", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "999px", padding: "4px" }}>
                        {["monthly", "annually"].map(b => (
                            <button
                                key={b}
                                onClick={() => setBilling(b)}
                                style={{
                                    padding: "7px 20px", borderRadius: "999px", border: "none",
                                    fontSize: "12.5px", fontWeight: 600, cursor: "pointer",
                                    background: billing === b ? "#fff" : "transparent",
                                    color: billing === b ? "#111" : "#6b7280",
                                    transition: "all 0.15s",
                                }}
                            >
                                {b === "annually" ? "Annual (save 20%)" : "Monthly"}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Cards */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>

                    {/* Starter */}
                    <div style={{ background: "#fff", borderRadius: "18px", padding: "28px", display: "flex", flexDirection: "column" }}>
                        <p style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "12px" }}>
                            Starter
                        </p>
                        <div style={{ display: "flex", alignItems: "flex-end", gap: "4px", marginBottom: "4px" }}>
                            <span style={{ fontSize: "40px", fontWeight: 800, color: "#111", lineHeight: 1 }}>
                                {annual ? STARTER.annual : STARTER.monthly}
                            </span>
                            <span style={{ fontSize: "13px", color: "#9ca3af", marginBottom: "6px" }}> / mo</span>
                        </div>
                        <p style={{ fontSize: "11.5px", color: "#9ca3af", marginBottom: "24px" }}>
                            {annual ? "billed annually" : "billed monthly"}
                        </p>

                        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", display: "flex", flexDirection: "column", gap: "10px", flex: 1 }}>
                            {STARTER.features.map(f => (
                                <li key={f} style={{ display: "flex", alignItems: "center", gap: "9px", fontSize: "13px", color: "#374151" }}>
                                    <Check /> {f}
                                </li>
                            ))}
                        </ul>

                        <button
                            onClick={() => handleSelect(STARTER.productId, `Starter ${billing}`)}
                            style={{
                                width: "100%", padding: "12px", fontSize: "13px", fontWeight: 700,
                                background: "#111", color: "#fff", border: "none", borderRadius: "10px",
                                cursor: "pointer", transition: "opacity 0.15s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = "0.82"}
                            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                        >
                            Get started
                        </button>
                    </div>

                    {/* Growth */}
                    <div style={{ background: "linear-gradient(155deg, #012b1e, #047857)", borderRadius: "18px", padding: "28px", display: "flex", flexDirection: "column" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
                            <p style={{ fontSize: "11px", fontWeight: 700, color: "#4ade80", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                                Growth
                            </p>
                            {/* Tier toggle */}
                            <div style={{ display: "flex", background: "rgba(255,255,255,0.1)", borderRadius: "999px", padding: "3px", gap: "2px" }}>
                                {["S", "M", "L"].map(t => (
                                    <button
                                        key={t}
                                        onClick={() => setGrowthTier(t)}
                                        style={{
                                            width: "28px", height: "24px", borderRadius: "999px", border: "none",
                                            fontSize: "11px", fontWeight: 700, cursor: "pointer",
                                            background: growthTier === t ? "#fff" : "transparent",
                                            color: growthTier === t ? "#15803d" : "rgba(255,255,255,0.6)",
                                            transition: "all 0.15s",
                                        }}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "flex-end", gap: "4px", marginBottom: "4px" }}>
                            <span style={{ fontSize: "40px", fontWeight: 800, color: "#fff", lineHeight: 1 }}>
                                {annual ? tier.annual : tier.monthly}
                            </span>
                            <span style={{ fontSize: "13px", color: "rgba(255,255,255,0.45)", marginBottom: "6px" }}> / mo</span>
                        </div>
                        <p style={{ fontSize: "11.5px", color: "rgba(255,255,255,0.4)", marginBottom: "24px" }}>
                            {annual ? "billed annually" : "billed monthly"}
                        </p>

                        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", display: "flex", flexDirection: "column", gap: "10px", flex: 1 }}>
                            {[
                                `${tier.accounts} customer accounts`,
                                `${tier.stores} Shopify store${tier.stores !== "1" ? "s" : ""}`,
                                `${tier.events} credit events / mo`,
                                tier.sms,
                                ...GROWTH_SHARED_FEATURES,
                            ].map(f => (
                                <li key={f} style={{ display: "flex", alignItems: "center", gap: "9px", fontSize: "13px", color: "rgba(255,255,255,0.85)" }}>
                                    <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                        <polyline points="20 6 9 17 4 12"/>
                                    </svg>
                                    {f}
                                </li>
                            ))}
                        </ul>

                        <button
                            onClick={() => handleSelect(tier.productId, `Growth ${growthTier} ${billing}`)}
                            style={{
                                width: "100%", padding: "12px", fontSize: "13px", fontWeight: 700,
                                background: "#22c55e", color: "#fff", border: "none", borderRadius: "10px",
                                cursor: "pointer", transition: "opacity 0.15s",
                                boxShadow: "0 4px 14px rgba(34,197,94,0.35)",
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
                            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                        >
                            Get started
                        </button>
                    </div>

                </div>

                {/* Footer note */}
                <p style={{ textAlign: "center", fontSize: "12px", color: "#4b5563", marginTop: "24px" }}>
                    No setup fees · 14-day free trial · Cancel anytime
                </p>

            </div>
        </div>
    );
}
